import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  assertSafeSessionValue,
  createScalarDraftAdapter,
  defineNavigationQuerySchema,
  navigationQuery,
  useNavigationQuery,
  useNavigationScope,
  useNavigateWithOrigin,
  type SafeSessionValue,
  type SessionStateAdapter,
} from '../../navigation';

const q = () => navigationQuery.string('q', { defaultValue: '', maxLength: 120 });
const page = () => navigationQuery.integer('page', { defaultValue: 1, min: 1, max: 9999 });
const pageSize = () => navigationQuery.enum('pageSize', ['10', '20', '50'] as const, '20');
const branch = () => navigationQuery.string('branch', { defaultValue: '', maxLength: 80 });

export const requestsSchema = defineNavigationQuerySchema({
  status: navigationQuery.enum('status', ['pending', 'approved', 'rejected', 'all'] as const, 'pending'),
  q: q(),
  course: navigationQuery.string('course', { defaultValue: '', maxLength: 100 }),
  groupCode: navigationQuery.string('groupCode', { defaultValue: '', maxLength: 80 }),
  branch: branch(),
  page: page(),
  pageSize: pageSize(),
  request: navigationQuery.id('request'),
});

export const interestsSchema = defineNavigationQuerySchema({
  status: navigationQuery.enum(
    'status',
    ['pending', 'notified', 'rejected', 'converted', 'paid', 'all'] as const,
    'pending',
  ),
  q: q(),
  course: navigationQuery.string('course', { defaultValue: '', maxLength: 100 }),
  branch: branch(),
  created_date: navigationQuery.date('created_date'),
  page: page(),
  pageSize: pageSize(),
  interest: navigationQuery.id('interest'),
  interestPanel: navigationQuery.enum(
    'interestPanel',
    ['', 'notify', 'reject', 'group'] as const,
    '',
  ),
});

export const slotsSchema = defineNavigationQuerySchema({
  q: q(),
  branch: branch(),
  place: navigationQuery.string('place', { defaultValue: '', maxLength: 100 }),
  type: navigationQuery.string('type', { defaultValue: '', maxLength: 50 }),
  active: navigationQuery.enum('active', ['all', 'active', 'inactive'] as const, 'all'),
  date: navigationQuery.date('date'),
  page: page(),
  pageSize: pageSize(),
  slot: navigationQuery.id('slot'),
  slotPanel: navigationQuery.enum('slotPanel', ['', 'add', 'edit'] as const, ''),
});

export const consultQueueSchema = defineNavigationQuerySchema({
  q: q(),
  status: navigationQuery.enum(
    'status',
    ['pending', 'selected', 'attended', 'cancelled', 'all'] as const,
    'pending',
  ),
  branch: branch(),
  course: navigationQuery.string('course', { defaultValue: '', maxLength: 100 }),
  date: navigationQuery.date('date'),
  page: page(),
  pageSize: pageSize(),
  consult: navigationQuery.id('consult'),
});

export const tasksSchema = defineNavigationQuerySchema({
  status: navigationQuery.enum(
    'status',
    ['pending', 'in_progress', 'completed', 'all'] as const,
    'pending',
  ),
  priority: navigationQuery.enum(
    'priority',
    ['all', 'low', 'medium', 'high', 'urgent'] as const,
    'all',
  ),
  days: navigationQuery.enum('days', ['all', '1', '7', '30'] as const, 'all'),
  type: navigationQuery.enum(
    'type',
    [
      'all',
      'approve_student',
      'create_group',
      'upload_material',
      'review_invoice',
      'approve_request',
      'review_interest',
      'add_to_group',
      'cancel_session',
      'deactivate_student',
      'review_comment',
      'group_followup',
      'daily_session_followup',
      'other',
    ] as const,
    'all',
  ),
  q: q(),
  task: navigationQuery.id('task'),
  taskPanel: navigationQuery.enum(
    'taskPanel',
    ['', 'workflow', 'complete', 'plan'] as const,
    '',
  ),
});

export const taskManagementSchema = defineNavigationQuerySchema({
  tab: navigationQuery.enum('tab', ['folders', 'overview'] as const, 'folders'),
  reviewTab: navigationQuery.enum('reviewTab', ['employee', 'all'] as const, 'employee'),
  status: navigationQuery.enum(
    'status',
    ['all', 'pending', 'in_progress', 'completed', 'draft'] as const,
    'all',
  ),
  priority: navigationQuery.enum(
    'priority',
    ['all', 'low', 'medium', 'high', 'urgent'] as const,
    'all',
  ),
  assignee: navigationQuery.id('assignee'),
  branch: branch(),
  page: page(),
  created: navigationQuery.enum('created', ['all', '1', '7', '30'] as const, 'all'),
  task: navigationQuery.id('task'),
  panel: navigationQuery.enum('panel', ['', 'create', 'edit', 'review'] as const, ''),
});

export const reportsSchema = defineNavigationQuerySchema({
  view: navigationQuery.enum('view', ['', 'activity', 'written'] as const, ''),
  day: navigationQuery.date('day'),
  activityEmployee: navigationQuery.id('activityEmployee'),
  activityDetail: navigationQuery.id('activityDetail'),
  activityPage: navigationQuery.integer('activityPage', { defaultValue: 1, min: 1, max: 9999 }),
  activityEventPage: navigationQuery.integer('activityEventPage', { defaultValue: 1, min: 1, max: 9999 }),
  tab: navigationQuery.enum('tab', ['unread', 'all'] as const, 'unread'),
  q: q(),
  branch: branch(),
  employee: navigationQuery.id('employee'),
  report: navigationQuery.id('report'),
});

export const logsSchema = defineNavigationQuerySchema({
  q: q(),
  page: page(),
  view: navigationQuery.enum('view', ['', 'pending', 'approved', 'rejected', 'audit'] as const, ''),
  requestType: navigationQuery.string('request_type', { defaultValue: '', maxLength: 80 }),
  employeeId: navigationQuery.string('employee_id', { defaultValue: '', maxLength: 12 }),
  dateFrom: navigationQuery.date('date_from'),
  dateTo: navigationQuery.date('date_to'),
  level: navigationQuery.enum('level', ['all', 'info', 'warning', 'danger'] as const, 'all'),
  status: navigationQuery.enum(
    'status',
    ['all', 'logged', 'pending', 'approved', 'rejected'] as const,
    'all',
  ),
  date: navigationQuery.date('date'),
  branch: branch(),
  sort: navigationQuery.enum('sort', ['id', 'action', 'employee_id', 'level', 'status'] as const, 'id'),
  dir: navigationQuery.enum('dir', ['asc', 'desc'] as const, 'desc'),
  metadata: navigationQuery.boolean('metadata', false),
  excludeAuth: navigationQuery.boolean('excludeAuth', true),
  log: navigationQuery.id('log'),
});

type WorkflowDraftScalar = string | number | boolean | null;

function validatedScalarDraftAdapter<T extends Record<string, WorkflowDraftScalar>>(
  defaults: T,
  validate: (value: T) => boolean,
): SessionStateAdapter<T> {
  const base = createScalarDraftAdapter<T>(defaults);
  return Object.freeze({
    initial: base.initial,
    encode(value: T): SafeSessionValue {
      if (!validate(value)) throw new Error('Invalid reception workflow draft');
      return base.encode(value);
    },
    decode(value: unknown): T | null {
      const decoded = base.decode(value);
      return decoded && validate(decoded) ? decoded : null;
    },
    isDirty: base.isDirty,
  });
}

const SLOT_SESSION_TYPES = new Set(['private_meeting', 'free_session', 'paid_session', 'consultation', 'introduction']);
const QUEUE_STATUSES = new Set(['pending', 'attended', 'cancelled']);
const TASK_PRIORITIES = new Set(['low', 'medium', 'high', 'urgent']);
const TASK_MANAGEMENT_TYPES = new Set([
  'daily_session_followup', 'other', 'review_invoice', 'group_followup', 'approve_student', 'review_comment',
]);
const TASK_PLAN_TYPES = new Set([
  'other', 'daily_session_followup', 'group_followup', 'review_invoice', 'approve_request', 'review_interest',
]);
const REVIEW_DECISIONS = new Set(['', 'self:approve', 'self:reject', 'other:done', 'other:not_yet', 'other:done_wrong']);
const LOCAL_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})?$/u;

function isBoundedText(value: string, maxLength: number): boolean {
  if (value.length > maxLength) return false;
  for (const character of value) {
    const code = character.charCodeAt(0);
    if (code <= 8 || code === 11 || code === 12 || (code >= 14 && code <= 31) || code === 127) return false;
  }
  return true;
}

export function isValidReceptionWorkflowDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function isValidReceptionWorkflowDateTime(value: string): boolean {
  if (value === '') return true;
  if (value.length > 40 || !LOCAL_DATE_TIME.test(value) || !isValidReceptionWorkflowDate(value.slice(0, 10))) return false;
  const hour = Number(value.slice(11, 13));
  const minute = Number(value.slice(14, 16));
  const secondsMatch = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:(\d{2})/u.exec(value);
  const second = secondsMatch ? Number(secondsMatch[1]) : 0;
  return hour <= 23 && minute <= 59 && second <= 59 && !Number.isNaN(Date.parse(value));
}

export const receptionWorkflowDraftAdapters = Object.freeze({
  interest: validatedScalarDraftAdapter<{ note: string; targetGroupId: number }>(
    { note: '', targetGroupId: 0 },
    (value) => isBoundedText(value.note, 500)
      && Number.isSafeInteger(value.targetGroupId)
      && value.targetGroupId >= 0
      && value.targetGroupId <= 2_147_483_647,
  ),
  slot: validatedScalarDraftAdapter<{
    course_name: string;
    place: string;
    date: string;
    time: string;
    session_type: string;
    branch: string;
    is_active: boolean;
  }>({
    course_name: '',
    place: 'Offline',
    date: '',
    time: '',
    session_type: 'free_session',
    branch: '',
    is_active: true,
  }, (value) => isBoundedText(value.course_name, 100)
    && isBoundedText(value.place, 120)
    && (value.date === '' || isValidReceptionWorkflowDate(value.date))
    && isBoundedText(value.time, 80)
    && SLOT_SESSION_TYPES.has(value.session_type)
    && isBoundedText(value.branch, 80)),
  queueStatus: validatedScalarDraftAdapter<{ status: string; touched: boolean }>(
    { status: 'pending', touched: false },
    (value) => QUEUE_STATUSES.has(value.status),
  ),
  taskCompletion: validatedScalarDraftAdapter<{ summary: string }>(
    { summary: '' },
    (value) => isBoundedText(value.summary, 4000),
  ),
  taskManagementForm: validatedScalarDraftAdapter<{
    taskType: string;
    assignee: string;
    priority: string;
    title: string;
    expiresAt: string;
    description: string;
  }>({
    taskType: 'daily_session_followup',
    assignee: 'all',
    priority: 'high',
    title: '',
    expiresAt: '',
    description: '',
  }, (value) => TASK_MANAGEMENT_TYPES.has(value.taskType)
    && (value.assignee === 'all' || /^[1-9]\d{0,9}$/u.test(value.assignee))
    && TASK_PRIORITIES.has(value.priority)
    && isBoundedText(value.title, 160)
    && isValidReceptionWorkflowDateTime(value.expiresAt)
    && isBoundedText(value.description, 1200)),
  taskManagementReview: validatedScalarDraftAdapter<{ decision: string; feedback: string }>(
    { decision: '', feedback: '' },
    (value) => REVIEW_DECISIONS.has(value.decision) && isBoundedText(value.feedback, 1000),
  ),
});

export interface ReceptionTaskPlanDraftEntry {
  title: string;
  description: string;
  taskType: string;
  priority: string;
  expiresAt: string;
  note: string;
}

export interface ReceptionTaskPlanDraft {
  general: string;
  items: ReceptionTaskPlanDraftEntry[];
}

export class ReceptionWorkflowHttpError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ReceptionWorkflowHttpError';
  }
}

const PLAN_STRING_LIMITS = Object.freeze({
  title: 160,
  description: 1200,
  taskType: 80,
  priority: 20,
  expiresAt: 40,
  note: 500,
});

function decodeTaskPlanDraft(value: unknown): ReceptionTaskPlanDraft | null {
  try {
    assertSafeSessionValue(value);
  } catch {
    return null;
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.general !== 'string' || !isBoundedText(record.general, 1000) || !Array.isArray(record.items)) return null;
  if (record.items.length < 1 || record.items.length > 10) return null;
  const items: ReceptionTaskPlanDraftEntry[] = [];
  for (const raw of record.items) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const entry = raw as Record<string, unknown>;
    const decoded = {} as ReceptionTaskPlanDraftEntry;
    for (const [key, limit] of Object.entries(PLAN_STRING_LIMITS) as [keyof ReceptionTaskPlanDraftEntry, number][]) {
      const field = entry[key];
      if (typeof field !== 'string' || !isBoundedText(field, limit)) return null;
      decoded[key] = field;
    }
    if (!TASK_PLAN_TYPES.has(decoded.taskType)
      || !TASK_PRIORITIES.has(decoded.priority)
      || !isValidReceptionWorkflowDateTime(decoded.expiresAt)) return null;
    items.push(decoded);
  }
  return { general: record.general, items };
}

const emptyTaskPlanDraft = (): ReceptionTaskPlanDraft => ({
  general: '',
  items: [{ title: '', description: '', taskType: 'other', priority: 'medium', expiresAt: '', note: '' }],
});

export const receptionTaskPlanDraftAdapter: SessionStateAdapter<ReceptionTaskPlanDraft> = Object.freeze({
  initial: emptyTaskPlanDraft,
  encode(value: ReceptionTaskPlanDraft): SafeSessionValue {
    const decoded = decodeTaskPlanDraft(value);
    if (!decoded) throw new Error('Invalid task plan draft');
    return decoded as unknown as SafeSessionValue;
  },
  decode(value: unknown): ReceptionTaskPlanDraft | null {
    return decodeTaskPlanDraft(value);
  },
  isDirty: (value: ReceptionTaskPlanDraft, initial: ReceptionTaskPlanDraft) => JSON.stringify(value) !== JSON.stringify(initial),
});

export type ReceptionWorkflowNavigationKind =
  | 'requests'
  | 'interests'
  | 'slots'
  | 'consult-queue'
  | 'tasks'
  | 'task-management'
  | 'reports'
  | 'logs';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/u;
const POSITIVE_ID = /^[1-9]\d*$/u;
const NON_NEGATIVE_INTEGER = /^(?:0|[1-9]\d*)$/u;

function validDate(value: string | null): value is string {
  if (!value || !ISO_DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function positiveId(value: string | null): number | null {
  if (!value || !POSITIVE_ID.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function moveAlias(params: URLSearchParams, alias: string, key: string) {
  if (!params.has(key) && params.has(alias)) params.set(key, params.get(alias) ?? '');
  params.delete(alias);
}

function normalizeBooleanAlias(
  params: URLSearchParams,
  alias: string,
  key: string,
  trueValue = '1',
  falseValue = '0',
) {
  if (!params.has(key)) {
    const value = params.get(alias)?.trim().toLowerCase();
    if (value === 'true' || value === '1') params.set(key, trueValue);
    else if (value === 'false' || value === '0') params.set(key, falseValue);
  }
  params.delete(alias);
}

function removeInvalidString(params: URLSearchParams, key: string, maxLength: number) {
  const value = params.get(key);
  if (value !== null && value.length > maxLength) params.delete(key);
}

function removeInvalidInteger(
  params: URLSearchParams,
  key: string,
  min: number,
  max: number,
  defaultValue?: number,
) {
  const raw = params.get(key);
  if (raw === null) return;
  const parsed = NON_NEGATIVE_INTEGER.test(raw) ? Number(raw) : Number.NaN;
  if (!Number.isSafeInteger(parsed) || parsed < min || parsed > max || parsed === defaultValue) {
    params.delete(key);
  }
}

function removeInvalidId(params: URLSearchParams, key: string) {
  if (params.has(key) && positiveId(params.get(key)) === null) params.delete(key);
}

function removeInvalidDate(params: URLSearchParams, key: string) {
  if (params.has(key) && !validDate(params.get(key))) params.delete(key);
}

function removeInvalidEnum(
  params: URLSearchParams,
  key: string,
  allowed: readonly string[],
  defaultValue?: string,
) {
  const value = params.get(key);
  if (value === null) return;
  if (!allowed.includes(value) || value === defaultValue) params.delete(key);
}

function normalizeCommon(params: URLSearchParams, options: { hasPageSize?: boolean } = {}) {
  removeInvalidString(params, 'q', 120);
  removeInvalidString(params, 'branch', 80);
  removeInvalidInteger(params, 'page', 1, 9999, 1);
  if (options.hasPageSize) removeInvalidEnum(params, 'pageSize', ['10', '20', '50'], '20');
}

export function canonicalizeReceptionWorkflowSearch(
  search: string,
  kind: ReceptionWorkflowNavigationKind,
): string {
  const params = new URLSearchParams(search);

  if (kind === 'requests') {
    moveAlias(params, 'group', 'groupCode');
    moveAlias(params, 'page_size', 'pageSize');
    normalizeCommon(params, { hasPageSize: true });
    removeInvalidEnum(params, 'status', ['pending', 'approved', 'rejected', 'all'], 'pending');
    removeInvalidString(params, 'course', 100);
    removeInvalidString(params, 'groupCode', 80);
    removeInvalidId(params, 'request');
  } else if (kind === 'interests') {
    moveAlias(params, 'createdDate', 'created_date');
    moveAlias(params, 'page_size', 'pageSize');
    normalizeCommon(params, { hasPageSize: true });
    removeInvalidEnum(
      params,
      'status',
      ['pending', 'notified', 'rejected', 'converted', 'paid', 'all'],
      'pending',
    );
    removeInvalidString(params, 'course', 100);
    removeInvalidDate(params, 'created_date');
    removeInvalidId(params, 'interest');
    removeInvalidEnum(params, 'interestPanel', ['', 'notify', 'reject', 'group'], '');
    if (!params.has('interest')) params.delete('interestPanel');
  } else if (kind === 'slots') {
    moveAlias(params, 'session_type', 'type');
    moveAlias(params, 'slot_date', 'date');
    normalizeBooleanAlias(params, 'is_active', 'active', 'active', 'inactive');
    moveAlias(params, 'page_size', 'pageSize');
    normalizeCommon(params, { hasPageSize: true });
    removeInvalidString(params, 'place', 100);
    removeInvalidString(params, 'type', 50);
    removeInvalidDate(params, 'date');
    const active = params.get('active')?.trim().toLowerCase();
    if (active === 'true' || active === '1') params.set('active', 'active');
    else if (active === 'false' || active === '0') params.set('active', 'inactive');
    else if (active === 'all') params.delete('active');
    else if (active !== 'active' && active !== 'inactive') params.delete('active');
    removeInvalidId(params, 'slot');
    removeInvalidEnum(params, 'slotPanel', ['', 'add', 'edit'], '');
    if (params.get('slotPanel') !== 'add' && !params.has('slot')) params.delete('slotPanel');
    if (params.get('slotPanel') === 'add') params.delete('slot');
  } else if (kind === 'consult-queue') {
    moveAlias(params, 'slot_date', 'date');
    moveAlias(params, 'page_size', 'pageSize');
    normalizeCommon(params, { hasPageSize: true });
    if (params.get('status') === 'called') params.set('status', 'attended');
    removeInvalidEnum(
      params,
      'status',
      ['pending', 'selected', 'attended', 'cancelled', 'all'],
      'pending',
    );
    removeInvalidString(params, 'course', 100);
    removeInvalidDate(params, 'date');
    removeInvalidId(params, 'consult');
  } else if (kind === 'tasks') {
    moveAlias(params, 'task_type', 'type');
    normalizeCommon(params);
    removeInvalidEnum(params, 'status', ['pending', 'in_progress', 'completed', 'all'], 'pending');
    removeInvalidEnum(params, 'priority', ['all', 'low', 'medium', 'high', 'urgent'], 'all');
    removeInvalidEnum(params, 'days', ['all', '1', '7', '30'], 'all');
    removeInvalidEnum(params, 'type', [
      'all',
      'approve_student',
      'create_group',
      'upload_material',
      'review_invoice',
      'approve_request',
      'review_interest',
      'add_to_group',
      'cancel_session',
      'deactivate_student',
      'review_comment',
      'group_followup',
      'daily_session_followup',
      'other',
    ], 'all');
    removeInvalidId(params, 'task');
    removeInvalidEnum(params, 'taskPanel', ['', 'workflow', 'complete', 'plan'], '');
    if (params.get('taskPanel') !== 'plan' && !params.has('task')) params.delete('taskPanel');
    if (params.get('taskPanel') === 'plan') params.delete('task');
  } else if (kind === 'task-management') {
    moveAlias(params, 'assigned_to', 'assignee');
    moveAlias(params, 'days', 'created');
    removeInvalidEnum(params, 'tab', ['folders', 'overview'], 'folders');
    removeInvalidEnum(params, 'reviewTab', ['employee', 'all'], 'employee');
    removeInvalidEnum(params, 'status', ['all', 'pending', 'in_progress', 'completed', 'draft'], 'all');
    removeInvalidEnum(params, 'priority', ['all', 'low', 'medium', 'high', 'urgent'], 'all');
    removeInvalidId(params, 'assignee');
    removeInvalidEnum(params, 'created', ['all', '1', '7', '30'], 'all');
    removeInvalidId(params, 'task');
    removeInvalidEnum(params, 'panel', ['', 'create', 'edit', 'review'], '');
    if (params.get('panel') !== 'create' && !params.has('task')) params.delete('panel');
    if (params.get('panel') === 'create') params.delete('task');
  } else if (kind === 'reports') {
    removeInvalidEnum(params, 'view', ['', 'activity', 'written'], '');
    removeInvalidDate(params, 'day');
    removeInvalidId(params, 'activityEmployee');
    removeInvalidId(params, 'activityDetail');
    moveAlias(params, 'employee_id', 'employee');
    if (!params.has('tab') && params.has('unread_only')) {
      const unreadOnly = params.get('unread_only')?.trim().toLowerCase();
      if (unreadOnly === 'false' || unreadOnly === '0') params.set('tab', 'all');
      else if (unreadOnly === 'true' || unreadOnly === '1') params.set('tab', 'unread');
    }
    params.delete('unread_only');
    normalizeCommon(params);
    removeInvalidEnum(params, 'tab', ['unread', 'all'], 'unread');
    removeInvalidId(params, 'employee');
    removeInvalidId(params, 'report');
  } else {
    moveAlias(params, 'sort_by', 'sort');
    moveAlias(params, 'sort_dir', 'dir');
    normalizeBooleanAlias(params, 'include_metadata', 'metadata');
    normalizeBooleanAlias(params, 'exclude_login_logout', 'excludeAuth');
    normalizeCommon(params);
    removeInvalidEnum(params, 'view', ['', 'pending', 'approved', 'rejected', 'audit'], '');
    removeInvalidString(params, 'request_type', 80);
    removeInvalidId(params, 'employee_id');
    removeInvalidDate(params, 'date_from');
    removeInvalidDate(params, 'date_to');
    removeInvalidEnum(params, 'level', ['all', 'info', 'warning', 'danger'], 'all');
    removeInvalidEnum(params, 'status', ['all', 'logged', 'pending', 'approved', 'rejected'], 'all');
    removeInvalidEnum(params, 'sort', ['id', 'action', 'employee_id', 'level', 'status'], 'id');
    removeInvalidDate(params, 'date');
    removeInvalidEnum(params, 'dir', ['asc', 'desc'], 'desc');
    if (params.get('metadata') !== '1') params.delete('metadata');
    if (params.get('excludeAuth') === 'true') params.set('excludeAuth', '1');
    else if (params.get('excludeAuth') === 'false') params.set('excludeAuth', '0');
    if (params.get('excludeAuth') !== '0') params.delete('excludeAuth');
    removeInvalidId(params, 'log');
  }

  const value = params.toString();
  return value ? `?${value}` : '';
}

function useCanonicalWorkflowSearch(kind: ReceptionWorkflowNavigationKind) {
  const location = useLocation();
  const navigate = useNavigate();
  const canonicalSearch = React.useMemo(
    () => canonicalizeReceptionWorkflowSearch(location.search, kind),
    [kind, location.search],
  );
  React.useEffect(() => {
    if (canonicalSearch === location.search) return;
    void navigate({
      pathname: location.pathname,
      search: canonicalSearch,
      hash: location.hash,
    }, { replace: true, state: location.state });
  }, [canonicalSearch, location.hash, location.pathname, location.search, location.state, navigate]);
}

export function useRequestsNavigation() {
  useCanonicalWorkflowSearch('requests');
  return useNavigationQuery(requestsSchema);
}

export function useInterestsNavigation() {
  useCanonicalWorkflowSearch('interests');
  return useNavigationQuery(interestsSchema);
}

export function useSlotsNavigation() {
  useCanonicalWorkflowSearch('slots');
  return useNavigationQuery(slotsSchema);
}

export function useConsultQueueNavigation() {
  useCanonicalWorkflowSearch('consult-queue');
  return useNavigationQuery(consultQueueSchema);
}

export function useTasksNavigation() {
  useCanonicalWorkflowSearch('tasks');
  return useNavigationQuery(tasksSchema);
}

export function useTaskManagementNavigation() {
  useCanonicalWorkflowSearch('task-management');
  return useNavigationQuery(taskManagementSchema);
}

export function useReportsNavigation() {
  useCanonicalWorkflowSearch('reports');
  return useNavigationQuery(reportsSchema);
}

export function useLogsNavigation() {
  useCanonicalWorkflowSearch('logs');
  return useNavigationQuery(logsSchema);
}

export type ReceptionWorkflowMutationCurrentness = () => boolean;

/**
 * Bind an async workflow mutation continuation to the exact principal,
 * authorization scope, route entry, and entity that started it.
 */
export function useReceptionWorkflowMutationGuard(
  workflowScopeIdentity: string,
  entityIdentity: string | number | null = null,
): (operationEntityIdentity?: string | number | null) => ReceptionWorkflowMutationCurrentness {
  const location = useLocation();
  const navigationScope = useNavigationScope();
  const identity = JSON.stringify([
    navigationScope.scopeId,
    navigationScope.userId,
    workflowScopeIdentity,
    location.key,
    location.pathname,
    location.search,
    location.hash,
    entityIdentity,
  ]);
  const identityRef = React.useRef(identity);
  const mountedRef = React.useRef(false);
  const operationGenerationRef = React.useRef(0);
  const operationEntityIdentityRef = React.useRef<string | number | null>(null);
  identityRef.current = identity;

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      operationGenerationRef.current += 1;
    };
  }, []);

  return React.useCallback((operationEntityIdentity = null) => {
    const capturedIdentity = identityRef.current;
    const capturedEntityIdentity = operationEntityIdentity;
    const capturedOperationGeneration = operationGenerationRef.current + 1;
    operationGenerationRef.current = capturedOperationGeneration;
    operationEntityIdentityRef.current = capturedEntityIdentity;
    return () => mountedRef.current
      && operationGenerationRef.current === capturedOperationGeneration
      && identityRef.current === capturedIdentity
      && operationEntityIdentityRef.current === capturedEntityIdentity;
  }, []);
}

export function sanitizeReceptionWorkflowInternalPath(
  value: string | null | undefined,
): string | null {
  const candidate = String(value ?? '').trim();
  if (!candidate.startsWith('/') || candidate.startsWith('//')) return null;
  try {
    const parsed = new URL(candidate, 'https://navigation.invalid');
    if (parsed.origin !== 'https://navigation.invalid') return null;
    if (parsed.hash) return null;
    const queryEntries = [...parsed.searchParams.entries()];
    const exactPositiveIdQuery = (key: string) => queryEntries.length === 1
      && queryEntries[0]?.[0] === key
      && positiveId(queryEntries[0]?.[1] ?? null) !== null;
    const noQuery = queryEntries.length === 0;
    const allowed = (
      parsed.pathname === '/reception/students'
        && (noQuery
          || exactPositiveIdQuery('student')
          || (queryEntries.length === 1
            && queryEntries[0]?.[0] === 'category'
            && queryEntries[0]?.[1] === 'pending'))
    ) || (
      parsed.pathname === '/reception/groups'
        && (noQuery || exactPositiveIdQuery('group'))
    ) || (
      parsed.pathname === '/reception/requests'
        && (noQuery || exactPositiveIdQuery('request'))
    ) || (
      parsed.pathname === '/reception/course-interests'
        && (noQuery || exactPositiveIdQuery('interest'))
    ) || (
      /^\/reception\/invoices\/[1-9]\d*$/u.test(parsed.pathname)
        && noQuery
        && positiveId(parsed.pathname.slice('/reception/invoices/'.length)) !== null
    ) || (
      (parsed.pathname === '/reception/language/materials'
        || parsed.pathname === '/reception/calendar')
        && noQuery
    );
    if (!allowed) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function useOpenTaskWork() {
  const navigateWithOrigin = useNavigateWithOrigin();
  return React.useCallback((task: { direct_link?: string | null }) => {
    const safeDirectLink = sanitizeReceptionWorkflowInternalPath(task.direct_link);
    if (safeDirectLink) navigateWithOrigin(safeDirectLink);
  }, [navigateWithOrigin]);
}

export const receptionWorkflowNavigationInternals = Object.freeze({
  positiveId,
  validDate,
});
